import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  REJECTION_REASONS,
  RejectionReasonKey,
} from "@/constants/rejectionReasons";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessName?: string;
  onSubmit: (reasons: string[], note?: string) => Promise<void> | void;
  submitting?: boolean;
}

export const RejectBusinessDialog = ({
  open,
  onOpenChange,
  businessName,
  onSubmit,
  submitting = false,
}: Props) => {
  const [selected, setSelected] = useState<Set<RejectionReasonKey>>(new Set());
  const [note, setNote] = useState("");

  const toggle = (key: RejectionReasonKey) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;
    await onSubmit(Array.from(selected), note.trim() || undefined);
    setSelected(new Set());
    setNote("");
  };

  const handleCancel = () => {
    setSelected(new Set());
    setNote("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Reject {businessName ?? "business"}</DialogTitle>
          <DialogDescription>
            Select one or more reasons. The business will see these in their
            rejection notice.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {(Object.keys(REJECTION_REASONS) as RejectionReasonKey[]).map(
            (key) => (
              <label
                key={key}
                className="flex items-start gap-2 p-2 rounded border border-border hover:bg-muted cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.has(key)}
                  onChange={() => toggle(key)}
                  className="mt-0.5"
                />
                <span className="text-sm">{REJECTION_REASONS[key]}</span>
              </label>
            ),
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="rej-note" className="text-sm">
            Additional note (optional)
          </Label>
          <Textarea
            id="rej-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Any extra detail for the business…"
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={selected.size === 0 || submitting}
          >
            Reject business
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
